import React, { useEffect, useState } from 'react';
import { X, Share2, Download, MessageCircle } from 'lucide-react';
import { renderShareCard } from './shareCard';
import { tierFor } from './tiers';

const NAME_KEY = 'skycatch_name';
const SITE = 'https://trucrunch.in/play';

export const ShareScoreModal = ({ score, bestStreak, initialName, onClose }) => {
  const [name, setName] = useState(() => initialName || localStorage.getItem(NAME_KEY) || '');
  const [blob, setBlob] = useState(null);
  const [url, setUrl] = useState('');
  const tier = tierFor(score);
  const text = `I scored ${score} on Sky Catch and I'm ranked ${tier.title}! Can you beat me? ${SITE}`;

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    let alive = true; let objUrl = '';
    const t = setTimeout(async () => {
      const b = await renderShareCard({ score, bestStreak, name: name.trim().slice(0, 20) });
      if (!alive) return;
      objUrl = URL.createObjectURL(b);
      setBlob(b); setUrl(objUrl);
    }, 250);
    return () => { alive = false; clearTimeout(t); if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [score, bestStreak, name]);

  const saveName = () => localStorage.setItem(NAME_KEY, name.trim());

  const download = () => {
    saveName();
    const a = document.createElement('a');
    a.href = url; a.download = `sky-catch-${score}.png`; a.click();
  };

  const share = async () => {
    saveName();
    const file = new File([blob], `sky-catch-${score}.png`, { type: 'image/png' });
    if (navigator.canShare?.({ files: [file] })) {
      try { await navigator.share({ files: [file], text }); } catch (e) { console.warn('[share] cancelled', e); }
      return;
    }
    download();
  };

  const whatsapp = () => window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener');

  return (
    <div data-testid="share-modal" className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-[#0B1020] text-white rounded-3xl ring-1 ring-white/10 p-5 w-full max-w-sm max-h-[95vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-lime-400">Share your score</div>
          <button data-testid="share-close-btn" onClick={onClose} aria-label="Close" className="opacity-70 hover:opacity-100"><X size={18} /></button>
        </div>
        <div className="mt-3 rounded-2xl overflow-hidden bg-slate-900" style={{ aspectRatio: '4 / 5' }}>
          {url ? <img data-testid="share-preview-img" src={url} alt="Score card" className="w-full h-full object-cover" />
            : <div className="w-full h-full animate-pulse bg-slate-800" />}
        </div>
        <input data-testid="share-name-input" value={name} maxLength={20} onChange={(e) => setName(e.target.value)}
          placeholder="Your name on the card (optional)"
          className="mt-3 w-full rounded-xl bg-white/10 px-4 py-2.5 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-lime-400" />
        <div className="mt-3 grid grid-cols-3 gap-2">
          <button data-testid="share-image-btn" disabled={!blob} onClick={share} className="col-span-3 inline-flex items-center justify-center gap-2 rounded-full bg-lime-400 text-[#0B1020] font-mono font-bold uppercase py-3 hover:bg-lime-300 active:scale-95 transition-transform disabled:opacity-50">
            <Share2 size={16} /> Share image
          </button>
          <button data-testid="share-download-btn" disabled={!blob} onClick={download} className="col-span-1 inline-flex items-center justify-center gap-1 rounded-full bg-white/10 py-2.5 text-xs font-mono uppercase hover:bg-white/20 disabled:opacity-50">
            <Download size={14} /> Save
          </button>
          <button data-testid="share-whatsapp-btn" onClick={whatsapp} className="col-span-2 inline-flex items-center justify-center gap-1 rounded-full bg-[#25D366] text-[#0B1020] py-2.5 text-xs font-mono font-bold uppercase hover:brightness-110">
            <MessageCircle size={14} /> WhatsApp
          </button>
        </div>
      </div>
    </div>
  );
};
